import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Menu,
  MenuItem,
  Box,
  Avatar,
  Divider,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Button,
  useTheme,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  LocalHospital as HospitalIcon,
  Help as HelpIcon,
} from '@mui/icons-material';
import { useAuth } from '../hooks/useAuth';
import SupportDialog from '../components/SupportDialog';

interface HeaderProps {
  onMenuClick?: () => void;
  showMenuButton?: boolean;
  variant?: 'default' | 'dashboard' | 'minimal';
}

const Header: React.FC<HeaderProps> = ({
  onMenuClick,
  showMenuButton = true,
  variant = 'default',
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { user, logout, isAuthenticated } = useAuth();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [supportDialogOpen, setSupportDialogOpen] = useState(false);

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    handleMenuClose();
    await logout();
  };

  const handleSettingsClick = () => {
    handleMenuClose();
    navigate('/settings');
  };

  const handleHelpClick = () => {
    handleMenuClose();
    setSupportDialogOpen(true);
  };

  const handleCloseSupportDialog = () => {
    setSupportDialogOpen(false);
  };

  const getUserInitials = () => {
    if (!user) return 'U';

    // Try firstName and lastName first
    if (user.firstName && user.lastName) {
      return `${user.firstName[0]}${user.lastName[0]}`.toUpperCase();
    }

    // Fallback to name if it exists
    if (user.name) {
      const names = user.name.split(' ');
      if (names.length >= 2) {
        return `${names[0][0]}${names[names.length - 1][0]}`.toUpperCase();
      }
      return user.name.substring(0, 2).toUpperCase();
    }

    // Final fallback
    if (user.firstName) {
      return user.firstName.substring(0, 2).toUpperCase();
    }

    return user.email?.substring(0, 2).toUpperCase() || 'U';
  };

  const getRoleColor = () => {
    switch (user?.role) {
      case 'admin':
        return theme.palette.error.main;
      case 'doctor':
        return theme.palette.primary.main;
      case 'assistant':
        return theme.palette.warning.main;
      default:
        return theme.palette.grey[500];
    }
  };

  const getRoleLabel = () => {
    switch (user?.role) {
      case 'admin':
        return 'Yönetici';
      case 'doctor':
        return 'Doktor';
      case 'assistant':
        return 'Asistan';
      default:
        return 'Kullanıcı';
    }
  };

  return (
    <AppBar
      position="fixed"
      sx={{
        zIndex: (theme) => theme.zIndex.drawer + 1,
        backgroundColor: variant === 'minimal' ? 'transparent' : undefined,
        boxShadow: variant === 'minimal' ? 'none' : undefined,
        borderRadius: 0,
      }}
    >
      <Toolbar>
        {/* Menu Button */}
        {showMenuButton && isAuthenticated && (
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={onMenuClick}
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>
        )}

        {/* Logo and Title */}
        <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
          <HospitalIcon sx={{ mr: 1 }} />
          <Typography
            variant="h6"
            noWrap
            component="div"
            sx={{
              cursor: 'pointer',
              display: { xs: 'none', sm: 'block' },
            }}
            onClick={() => navigate('/')}
          >
            DrSync
          </Typography>

          {/* Show role badge for authenticated users */}
          {user && (
            <Box
              sx={{
                ml: 2,
                px: 1.5,
                py: 0.5,
                borderRadius: 0,
                backgroundColor: `${getRoleColor()}20`,
                display: { xs: 'none', md: 'flex' },
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: getRoleColor(),
                  fontWeight: 600,
                  textTransform: 'uppercase',
                }}
              >
                {getRoleLabel()}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Right Side Actions */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {isAuthenticated ? (
            <>
              {/* Profile Menu */}
              <Tooltip title="Profil">
                <IconButton
                  edge="end"
                  aria-label="account of current user"
                  aria-controls="menu-appbar"
                  aria-haspopup="true"
                  onClick={handleProfileMenuOpen}
                  color="inherit"
                >
                  <Avatar
                    sx={{
                      width: 32,
                      height: 32,
                      bgcolor: getRoleColor(),
                      fontSize: '0.875rem',
                    }}
                    src={user.profileImage}
                  >
                    {getUserInitials()}
                  </Avatar>
                </IconButton>
              </Tooltip>
            </>
          ) : (
            <>
              <Button color="inherit" onClick={() => navigate('/login')}>
                Giriş Yap
              </Button>
              <Button
                color="inherit"
                variant="outlined"
                sx={{
                  borderColor: 'white',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  },
                }}
                onClick={() => navigate('/register')}
              >
                Kayıt Ol
              </Button>
            </>
          )}
        </Box>
      </Toolbar>

      {/* Profile Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        onClick={handleMenuClose}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: 'visible',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
            mt: 1.5,
            minWidth: 200,
            '& .MuiAvatar-root': {
              width: 32,
              height: 32,
              ml: -0.5,
              mr: 1,
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography variant="subtitle1" noWrap>
            {user?.name}
          </Typography>
          <Typography variant="body2" color="text.secondary" noWrap>
            {user?.email}
          </Typography>
        </Box>
        <Divider />

        <MenuItem onClick={handleSettingsClick}>
          <ListItemIcon>
            <SettingsIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Ayarlar</ListItemText>
        </MenuItem>

        <MenuItem onClick={handleHelpClick}>
          <ListItemIcon>
            <HelpIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Yardım</ListItemText>
        </MenuItem>

        <Divider />

        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>
            <Typography color="error">Çıkış Yap</Typography>
          </ListItemText>
        </MenuItem>
      </Menu>

      {/* Support Dialog */}
      <SupportDialog
        open={supportDialogOpen}
        onClose={handleCloseSupportDialog}
      />
    </AppBar>
  );
};

export default Header;